import { useState } from "react";
import { Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg, fmtDate, imgUrl, inr } from "@/lib/api";
import { useAdminApi } from "@/lib/admin";
import { useLoad, PageHead, DataTable, Td, NativeSelect } from "@/components/admin/kit";

const toForm = (p) => ({
  name: p?.name || "", description: p?.description || "", category_id: p?.category_id || "", image: p?.image || "",
  is_active: p?.is_active ?? true, is_featured: p?.is_featured ?? false, offer: p?.offer || "",
  variants: (p?.variants || [{ size: "", sku: "", price: 0, mrp: null, stock: 0, is_active: true }]).map((v) => ({ ...v, price: v.price / 100 || "", mrp: v.mrp ? v.mrp / 100 : "" })),
});

const ProductEditor = ({ product, cats, onClose }) => {
  const call = useAdminApi();
  const [p, setP] = useState(toForm(product));
  const setV = (i, k, val) => setP({ ...p, variants: p.variants.map((v, j) => (j === i ? { ...v, [k]: val } : v)) });
  const uploadImg = async (file) => {
    if (file.size > 10 * 1024 * 1024) return toast.error("File is larger than 10 MB. Please upload a file of 10 MB or less.");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await call("post", "/admin/media", fd);
      setP((cur) => ({ ...cur, image: r.url }));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const save = async () => {
    const body = { ...p, variants: p.variants.map((v) => ({ ...v, price: +v.price, mrp: v.mrp ? +v.mrp : null, stock: +v.stock })) };
    try {
      await (product?.id ? call("put", `/admin/products/${product.id}`, body) : call("post", "/admin/products", body));
      toast.success("Product saved");
      onClose(true);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="product-editor-dialog">
        <DialogHeader><DialogTitle>{product?.id ? "Edit product" : "New product"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1 sm:col-span-2"><Label>Name</Label><Input value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} data-testid="product-edit-name" /></div>
          <div className="space-y-1"><Label>Category</Label><NativeSelect value={p.category_id} onChange={(v) => setP({ ...p, category_id: v })} placeholder="Select…" options={cats.map((c) => [c.id, c.name])} testid="product-edit-category" /></div>
          <div className="flex items-end gap-3">
            <img src={imgUrl(p.image)} alt="" className="h-14 w-14 rounded-lg object-cover" />
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm"><Upload className="h-4 w-4" /> Image<input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => e.target.files[0] && uploadImg(e.target.files[0])} data-testid="product-edit-image" /></label>
          </div>
          <div className="space-y-1 sm:col-span-2"><Label>Offer ribbon (optional, e.g. Free gift with every 2 kg pack)</Label><Input maxLength={60} value={p.offer} onChange={(e) => setP({ ...p, offer: e.target.value })} data-testid="product-edit-offer" /></div>
          <div className="space-y-1 sm:col-span-2"><Label>Description</Label><Textarea value={p.description} onChange={(e) => setP({ ...p, description: e.target.value })} data-testid="product-edit-description" /></div>
        </div>
        <div className="space-y-2">
          <Label>Sizes / variants (price & MRP in ₹; leave MRP blank if no discount)</Label>
          {p.variants.map((v, i) => (
            <div key={i} className="grid grid-cols-6 gap-1.5" data-testid={`variant-row-${i}`}>
              <Input placeholder="Size" value={v.size} onChange={(e) => setV(i, "size", e.target.value)} data-testid={`variant-size-${i}`} />
              <Input placeholder="SKU" value={v.sku} onChange={(e) => setV(i, "sku", e.target.value)} data-testid={`variant-sku-${i}`} />
              <Input placeholder="Price" type="number" value={v.price} onChange={(e) => setV(i, "price", e.target.value)} data-testid={`variant-price-${i}`} />
              <Input placeholder="MRP" type="number" value={v.mrp ?? ""} onChange={(e) => setV(i, "mrp", e.target.value)} data-testid={`variant-mrp-${i}`} />
              <Input placeholder="Stock" type="number" value={v.stock} onChange={(e) => setV(i, "stock", e.target.value)} data-testid={`variant-stock-${i}`} />
              <div className="flex items-center gap-1"><Switch checked={v.is_active} onCheckedChange={(x) => setV(i, "is_active", x)} /><button onClick={() => setP({ ...p, variants: p.variants.filter((_, j) => j !== i) })} aria-label="Remove variant"><Trash2 className="h-4 w-4 text-slate-400" /></button></div>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setP({ ...p, variants: [...p.variants, { size: "", sku: "", price: "", mrp: "", stock: 0, is_active: true }] })} data-testid="variant-add-button"><Plus className="h-3.5 w-3.5" /> Add size</Button>
        </div>
        <div className="flex gap-6 text-sm">
          <label className="flex items-center gap-2"><Switch checked={p.is_active} onCheckedChange={(v) => setP({ ...p, is_active: v })} data-testid="product-edit-active" /> Enabled</label>
          <label className="flex items-center gap-2"><Switch checked={p.is_featured} onCheckedChange={(v) => setP({ ...p, is_featured: v })} data-testid="product-edit-featured" /> Featured</label>
        </div>
        <Button onClick={save} data-testid="product-save-button">Save product</Button>
      </DialogContent>
    </Dialog>
  );
};

const Categories = ({ cats, reload, isAdmin }) => {
  const call = useAdminApi();
  const [name, setName] = useState("");
  const add = async () => {
    try {
      await call("post", "/admin/categories", { name });
      setName("");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const toggle = (c) => call("put", `/admin/categories/${c.id}`, { name: c.name, description: c.description, image: c.image, sort_order: c.sort_order, is_active: !c.is_active }).then(reload);
  return (
    <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-5">
      <h2 className="mb-3 font-display font-bold">Categories</h2>
      <div className="flex flex-wrap gap-2">
        {cats.map((c) => <button key={c.id} disabled={!isAdmin} onClick={() => toggle(c)} className={`rounded-full border px-3 py-1 text-sm ${c.is_active ? "border-slate-900" : "border-dashed text-slate-400 line-through"}`} title="Click to enable/disable" data-testid={`admin-category-${c.slug}`}>{c.name}</button>)}
      </div>
      {isAdmin && <div className="mt-3 flex gap-2"><Input className="h-9 w-60" placeholder="New category name" value={name} onChange={(e) => setName(e.target.value)} data-testid="category-new-input" /><Button className="h-9" disabled={name.length < 2} onClick={add} data-testid="category-add-button">Add category</Button></div>}
    </div>
  );
};

export default function Products({ isAdmin }) {
  const [products, reload] = useLoad("/admin/products");
  const [cats, reloadCats] = useLoad("/admin/categories");
  const [logs] = useLoad("/admin/inventory-logs");
  const [edit, setEdit] = useState(null);
  const catName = Object.fromEntries((cats || []).map((c) => [c.id, c.name]));
  return (
    <div>
      <PageHead title="Products & inventory">{isAdmin && <Button onClick={() => setEdit({})} data-testid="product-add-button"><Plus className="h-4 w-4" /> Add product</Button>}</PageHead>
      {cats && <Categories cats={cats} reload={reloadCats} isAdmin={isAdmin} />}
      <DataTable head={["Product", "Category", "Sizes · price · stock", "Sold", "Status", ""]} testid="products-table">
        {products?.map((p) => (
          <tr key={p.id} data-testid={`admin-product-row-${p.id}`}>
            <Td><div className="flex items-center gap-3"><img src={imgUrl(p.image)} alt="" className="h-10 w-10 rounded object-cover" /><span className="font-semibold">{p.name}</span></div></Td>
            <Td>{catName[p.category_id]}</Td>
            <Td className="text-xs">{p.variants.map((v) => <p key={v.sku}><span className="font-mono-num">{v.sku}</span> · {v.size} · {inr(v.price)}{v.mrp ? ` (MRP ${inr(v.mrp)})` : ""} · <strong className={v.stock <= 10 ? "text-red-600" : ""}>{v.stock}</strong></p>)}</Td>
            <Td>{p.sold_count || 0}</Td><Td>{p.is_active ? "Enabled" : "Disabled"}{p.is_featured && " · Featured"}</Td>
            <Td>{isAdmin && <Button size="sm" variant="outline" onClick={() => setEdit(p)} data-testid={`product-edit-button-${p.id}`}>Edit</Button>}</Td>
          </tr>
        ))}
      </DataTable>
      <h2 className="mb-3 mt-10 font-display text-xl font-bold">Inventory changes</h2>
      <DataTable head={["Date", "SKU", "Change", "Reason"]} testid="inventory-logs-table">
        {logs?.slice(0, 50).map((l) => <tr key={l.id}><Td className="text-xs">{fmtDate(l.created_at, true)}</Td><Td className="font-mono-num text-xs">{l.sku}</Td><Td className={l.change < 0 ? "text-red-600" : "text-emerald-700"}>{l.change > 0 ? "+" : ""}{l.change}</Td><Td className="text-xs">{l.reason}</Td></tr>)}
      </DataTable>
      {edit && cats && <ProductEditor product={edit.id ? edit : null} cats={cats} onClose={(c) => { setEdit(null); if (c) reload(); }} />}
    </div>
  );
}
